// ============================================================
// ✅ MenuPage.jsx - V9 Final - Solde Progressif + Planning Visible par Tous
// + Masquage de l'observateur lui-même
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Clock, CheckCircle, Users, RefreshCw, XCircle, CalendarDays, Gift, PiggyBank } from 'lucide-react';
import NouvelleDemandeModal from './demandes/NouvelleDemandeModal';

export default function MenuPage() {
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [demandes, setDemandes] = useState([]);
  const [feries, setFeries] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [stats, setStats] = useState({ EnAttente: 0, Approuvees: 0, Refusees: 0 });
  const [soldeCA, setSoldeCA] = useState(null);
  const [soldesEmployes, setSoldesEmployes] = useState({});
  const [soldeProgressif, setSoldeProgressif] = useState(null);

  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [showDemandeModal, setShowDemandeModal] = useState(false);
  const [prefillDateDebut, setPrefillDateDebut] = useState('');
  const [prefillDateFin, setPrefillDateFin] = useState('');

  const aujourdHui = new Date();
  const demain = new Date(aujourdHui); demain.setDate(demain.getDate() + 1);
  const moisFrancais = format(currentMonth, 'MMMM yyyy', { locale: fr });
  const joursDuMois = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });

  // ✅ Détecter si l'utilisateur est un observateur (user sans manager)
  const isObservateur = user?.Role === 'observateur';

  useEffect(() => { if (user) loadAll(); }, [currentMonth, user]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const annee = currentMonth.getFullYear();
      const [feriesRes, employesRes, demandesRes, statsRes] = await Promise.all([
        api.get('/feries', { params: { annee } }),
        api.get('/employes', { params: { ID_Societe: user?.ID_Societe || null } }),
        api.get('/demandes/calendrier', { params: { annee } }),
        api.get('/demandes/stats', { params: { employeId: user.id } }),
      ]);

      setFeries(feriesRes.data.feries || []);
      setDemandes(demandesRes.data.demandes || []);
      setStats(statsRes.data.stats || {});

      // ✅ Tout le monde voit tout le monde (sauf admins)
      // ✅ L'observateur ne voit pas sa propre ligne
      let listeEmployes = (employesRes.data.employes || []).filter(e => {
    if (e.Role === 'admin') return false;
    if (e.Role === 'observateur') return false;  // ✅ Masquer les observateurs
    return true;
});
      setEmployes(listeEmployes);

      // ✅ Solde progressif pour chaque employé
      const soldesFiltres = {};
      for (const emp of listeEmployes) {
        const empId = emp.ID_Employes || emp.id;
        try {
          const res = await api.get(`/demandes/regles/${empId}/1`);
          if (res.data.success && res.data.calcul) {
            soldesFiltres[empId] = Math.round(res.data.calcul.JoursRestants * 10) / 10;
          }
        } catch (err) { /* Silencieux */ }
      }
      setSoldesEmployes(soldesFiltres);

      // ✅ Mon solde progressif (pas pour admin ni observateur)
      if (user?.Role !== 'admin' && !isObservateur) {
        const monSolde = soldesFiltres[user.id];
        setSoldeCA(monSolde !== undefined ? { JoursRestants: monSolde } : null);
        try {
          const res = await api.get(`/demandes/regles/${user.id}/1`);
          if (res.data.success && res.data.calcul) {
            setSoldeProgressif(res.data.calcul);
          }
        } catch (err) { /* Silencieux */ }
      } else {
        setSoldeCA(null);
        setSoldeProgressif(null);
      }

    } catch (err) { /* Silencieux */ }
    finally { setLoading(false); }
  };

  const getFerieForDay = (date) => feries.find(f => isSameDay(new Date(f.DateFerie), date));

  const getDemandesForEmployeDay = (employeId, date) => demandes.filter(d => {
    const empId = d.ID_Employe || d.employeId || d.ID_Employes;
    if (parseInt(empId) !== parseInt(employeId)) return false;
    const debut = d.DateDebut ? new Date(d.DateDebut) : null;
    const fin = d.DateFin ? new Date(d.DateFin) : null;
    if (!debut || !fin) return false;
    const dc = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return dc >= new Date(debut.getFullYear(), debut.getMonth(), debut.getDate()) && dc <= new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
  });

  const getCellStyle = (employeId, date) => {
    const js = getDay(date);
    const ferie = getFerieForDay(date);
    const demandesJour = getDemandesForEmployeDay(employeId, date);
    const isToday = isSameDay(date, aujourdHui);
    if (js === 0 || js === 6) return isToday ? 'bg-emerald-100 text-gray-400 border-emerald-300' : 'bg-gray-200 text-gray-400 border-gray-300';
    if (isToday) return ferie?.EstFerieChome ? 'bg-emerald-200 text-orange-700 border-emerald-400 font-medium' : 'bg-emerald-200 text-emerald-800 border-emerald-400 font-medium';
    if (ferie?.EstFerieChome) return 'bg-orange-100 text-orange-700 border-orange-300 font-medium';
    if (demandesJour.length > 0) {
      const s = demandesJour[0].StatusId || demandesJour[0].statusId;
      if (s === 1) return 'bg-gray-300 text-gray-700 border-gray-400';
      if (s === 2) return 'bg-amber-300 text-amber-800 border-amber-400';
      if (s === 3) return 'bg-emerald-500 text-white border-emerald-600 font-bold';
      if (s === 4) return 'bg-red-300 text-red-800 border-red-400';
      if (s === 5) return 'bg-purple-300 text-purple-800 border-purple-400';
    }
    return 'bg-white hover:bg-emerald-50 border-gray-200';
  };

  const getCellIcon = (employeId, date) => {
    const js = getDay(date); const ferie = getFerieForDay(date);
    const demandesJour = getDemandesForEmployeDay(employeId, date);
    const isToday = isSameDay(date, aujourdHui);
    if (js === 0 || js === 6) return '';
    if (ferie?.EstFerieChome) return '⭐';
    if (isToday) return '';
    if (demandesJour.length > 0) {
      const s = demandesJour[0].StatusId || demandesJour[0].statusId;
      if (s === 1) return '📝'; if (s === 2) return '⏳'; if (s === 3) return '✅'; if (s === 4) return '❌'; if (s === 5) return '🔄';
    }
    return '';
  };

  const getTooltip = (emp, date) => {
    const js = getDay(date); const ferie = getFerieForDay(date);
    const demandesJour = getDemandesForEmployeDay(emp.ID_Employes || emp.id, date);
    const ds = format(date, 'd MMM', { locale: fr });
    if (js === 0 || js === 6) return `Week-end - ${ds}`;
    if (ferie?.EstFerieChome) return `🎌 ${ferie.Libelle} - ${ds}`;
    if (demandesJour.length > 0) { const d=demandesJour[0]; return `${d.TypeCongeLibelle||'Congé'} (${d.StatusNom||'?'}) - ${ds}`; }
    return `${emp.Prenom||''} ${emp.Nom||''} - Présent - ${ds}`;
  };

  const isTraversable = (date) => { if (date < demain) return false; if (user?.Role==='user' && !isObservateur) { if (getDemandesForEmployeDay(user.id,date).length>0) return false; } return true; };
  const isSelectable = (date) => { if (!isTraversable(date)) return false; const js=getDay(date); return js!==0 && js!==6; };
  const handleCellMouseDown = (date) => { if (!isSelectable(date) || isObservateur) return; setSelectionStart(date); setSelectionEnd(date); setIsSelecting(true); };
  const handleCellMouseEnter = (date) => { if (!isSelecting||!isTraversable(date)||isObservateur) return; const debut=selectionStart<date?selectionStart:date; const fin=selectionStart<date?date:selectionStart; let ok=true; const d=new Date(debut); while(d<=fin){ if(!isTraversable(d)){ok=false;break;} d.setDate(d.getDate()+1); } if(ok) setSelectionEnd(date); };
  const handleCellMouseUp = () => { if(selectionStart&&selectionEnd){ const debut=selectionStart<selectionEnd?selectionStart:selectionEnd; const fin=selectionStart<selectionEnd?selectionEnd:selectionStart; setPrefillDateDebut(format(debut,'yyyy-MM-dd')); setPrefillDateFin(format(fin,'yyyy-MM-dd')); setShowDemandeModal(true); } setIsSelecting(false); setSelectionStart(null); setSelectionEnd(null); };
  const isInSelection = (date) => { if(!selectionStart||!selectionEnd||!isSelecting) return false; const debut=selectionStart<selectionEnd?selectionStart:selectionEnd; const fin=selectionStart<selectionEnd?selectionEnd:selectionStart; return date>=debut&&date<=fin&&isTraversable(date); };
  const changeMonth = (d) => setCurrentMonth(p=>{ const n=new Date(p); n.setMonth(n.getMonth()+d); return n; });

  return (
    <div className="p-3 md:p-5 bg-gray-50 min-h-screen" onMouseUp={handleCellMouseUp}>
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            👋 Bonjour, {user?.Prenom||user?.Nom}
            {isObservateur && <span className="text-sm font-normal text-amber-600 ml-2">👁️ Observateur</span>}
          </h2>
          <p className="text-sm text-gray-500">Tableau de bord • {user?.SocieteNom||'GestCongés'}</p>
        </div>
        <button onClick={loadAll} className="p-2 text-gray-400 hover:text-emerald-600 transition"><RefreshCw size={18} /></button>
      </div>

      {/* KPI CARDS - Masquées pour observateur */}
      {!isObservateur && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          {[
            { label:'En attente',value:stats.EnAttente||0,icon:<Clock size={20}/>,color:'text-amber-600',bg:'bg-amber-50' },
            { label:'Approuvés',value:stats.Approuvees||0,icon:<CheckCircle size={20}/>,color:'text-emerald-600',bg:'bg-emerald-50' },
            { label:'Refusés',value:stats.Refusees||0,icon:<XCircle size={20}/>,color:'text-red-600',bg:'bg-red-50' },
            { label:'Équipe',value:employes.length,icon:<Users size={20}/>,color:'text-blue-600',bg:'bg-blue-50' },
            { label:'Solde progressif',value:soldeCA?`${soldeCA.JoursRestants} j`:'—',icon:<PiggyBank size={20}/>,color:'text-purple-600',bg:'bg-purple-50' }
          ].map((item,idx)=>(
            <div key={idx} className={`${item.bg} rounded-xl p-4 shadow-sm border border-gray-100`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{item.label}</p>
                  <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                  {item.label === 'Solde progressif' && soldeProgressif && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      {soldeProgressif.MoisEcoules} mois × {Math.round(soldeProgressif.TauxProgressif * 100) / 100}j
                      {soldeProgressif.SoldeDemarrage > 0 && ` + ${soldeProgressif.SoldeDemarrage}j report`}
                    </p>
                  )}
                </div>
                <div className={item.color}>{item.icon}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CALENDRIER */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6 select-none">
        <div className="px-4 py-3 border-b bg-gradient-to-r from-emerald-50 to-green-50 flex items-center justify-between">
          <div className="flex items-center gap-3"><CalendarDays size={20} className="text-emerald-600"/><h3 className="font-semibold text-gray-800 capitalize">{moisFrancais}</h3></div>
          <div className="flex items-center gap-2">
            <button onClick={()=>changeMonth(-1)} className="p-1.5 rounded-lg hover:bg-emerald-100 text-gray-500"><ChevronLeft size={18}/></button>
            <button onClick={()=>setCurrentMonth(new Date())} className="px-3 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-lg font-medium">Aujourd'hui</button>
            <button onClick={()=>changeMonth(1)} className="p-1.5 rounded-lg hover:bg-emerald-100 text-gray-500"><ChevronRight size={18}/></button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {loading ? <div className="p-12 text-center text-gray-400">Chargement...</div> : (
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-gray-50">
                <th className="sticky left-0 bg-gray-50 z-10 px-3 py-2 text-left font-medium text-gray-500 border-b min-w-[140px]">Employé</th>
                <th className="px-2 py-2 text-center font-medium text-gray-500 border-b w-14">Solde</th>
                {joursDuMois.map((jour,idx)=>{ const isWE=getDay(jour)===0||getDay(jour)===6; const isToday=isSameDay(jour,aujourdHui); return (<th key={idx} className={`w-9 px-1 py-2 text-center font-medium border-b ${isToday?'bg-emerald-200 text-emerald-800':isWE?'bg-gray-200 text-gray-400':'text-gray-600'}`}><div className="text-[11px]">{format(jour,'d')}</div><div className="text-[9px]">{format(jour,'EE',{locale:fr}).substring(0,1)}</div></th>); })}
              </tr></thead>
              <tbody>
                {employes.length===0 ? <tr><td colSpan={joursDuMois.length+2} className="text-center py-8 text-gray-400">Aucun employé</td></tr> : employes.map(emp=>(
                  <tr key={emp.ID_Employes||emp.id}>
                    <td className="sticky left-0 bg-white z-10 px-3 py-2 border-b font-medium text-gray-700 whitespace-nowrap">{emp.Prenom} {emp.Nom}</td>
                    <td className="px-2 py-2 text-center border-b text-xs font-bold text-emerald-700 bg-emerald-50">
                      {soldesEmployes[emp.ID_Employes||emp.id] !== undefined ? `${soldesEmployes[emp.ID_Employes||emp.id]} j` : '...'}
                    </td>
                    {joursDuMois.map((jour,idx)=>{
                      const empId=emp.ID_Employes||emp.id; const cellStyle=getCellStyle(empId,jour); const icon=getCellIcon(empId,jour);
                      const tooltip=getTooltip(emp,jour); const traversable=isTraversable(jour) && !isObservateur; const inSel=isInSelection(jour);
                      const isMyRow=emp.ID_Employes===user.id||emp.id===user.id;
                      return (<td key={idx} className={`w-9 px-0 py-2 text-center border border-gray-100 text-[10px] transition ${cellStyle} ${traversable&&isMyRow?'cursor-pointer hover:scale-110':'cursor-not-allowed opacity-60'} ${inSel?'ring-2 ring-emerald-400 bg-emerald-100 scale-110':''}`} title={tooltip} onMouseDown={()=>isMyRow&&!isObservateur?handleCellMouseDown(jour):null} onMouseEnter={()=>isMyRow&&!isObservateur?handleCellMouseEnter(jour):null}>{icon}</td>);
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-3 border-t bg-gray-50 flex flex-wrap items-center gap-3 text-xs">
          <span className="font-medium text-gray-600">Légende :</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-white border rounded"></span> Présent</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-gray-300 rounded"></span> 📝 Brouillon</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-amber-300 rounded"></span> ⏳ En attente</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-emerald-500 rounded"></span> ✅ Approuvé</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-300 rounded"></span> ❌ Refusé</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-orange-100 rounded"></span> ⭐ Férié</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-gray-200 rounded"></span> Week-end</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-emerald-200 rounded"></span> Aujourd'hui</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-purple-300 rounded"></span> 🔄 Annulé</span>
        </div>
      </div>

      {/* FÉRIÉS DU MOIS */}
      {feries.filter(f=>{const d=new Date(f.DateFerie);return d>=startOfMonth(currentMonth)&&d<=endOfMonth(currentMonth);}).length>0&&(
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
          <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2"><Gift size={16}/> Jours fériés ce mois</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {feries.filter(f=>{const d=new Date(f.DateFerie);return d>=startOfMonth(currentMonth)&&d<=endOfMonth(currentMonth);}).map((f,i)=>(<div key={i} className="text-xs text-amber-700 bg-white rounded-lg px-3 py-2 border border-amber-200"><span className="font-medium">{f.DateFormatee}</span><span className="mx-1">•</span><span>{f.Libelle}</span></div>))}
          </div>
        </div>
      )}

      {/* MODALE - Masquée pour observateur */}
      {!isObservateur && (
        <NouvelleDemandeModal isOpen={showDemandeModal} onClose={()=>setShowDemandeModal(false)} onSuccess={loadAll} prefillDateDebut={prefillDateDebut} prefillDateFin={prefillDateFin}/>
      )}
    </div>
  );
}